import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import MobileMenu from './MobileMenu';
import UserProfileIndicator from './UserProfileIndicator';
import { useAuth } from '../../contexts/AuthContext';


const NavigationBar = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { userRole, userProfile, user, signOut } = useAuth();

  const allNavigationItems = [
    {
      label: 'Tableau de Bord',
      path: '/student-dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'Accédez à votre tableau de bord principal',
      roles: ['student']
    },
    {
      label: 'Chat IA',
      path: '/ai-chat-interface',
      icon: 'MessageSquare',
      tooltip: 'Discutez avec notre assistant IA',
      roles: ['student', 'parent', 'counselor']
    },
    {
      label: 'Quiz',
      path: '/interactive-quiz-system',
      icon: 'ClipboardList',
      tooltip: 'Passez des quiz interactifs',
      roles: ['student']
    },
    {
      label: 'Exploration',
      path: '/field-exploration-hub',
      icon: 'Compass',
      tooltip: 'Explorez les domaines de carrière',
      roles: ['student']
    },
    {
      label: 'Résultats',
      path: '/study-field-results',
      icon: 'TrendingUp',
      tooltip: 'Consultez vos résultats et recommandations',
      roles: ['student']
    },
    {
      label: 'Annuaire',
      path: '/school-directory',
      icon: 'School',
      tooltip: 'Explorez les établissements d\'enseignement',
      roles: ['student', 'parent', 'counselor']
    },
    {
      label: 'Espace Parent',
      path: '/parent-dashboard-interface',
      icon: 'Users',
      tooltip: 'Tableau de bord parental',
      roles: ['parent']
    },
    {
      label: 'Portail Conseiller',
      path: '/counselor-management-portal',
      icon: 'Briefcase',
      tooltip: 'Gestion des cas étudiants',
      roles: ['counselor']
    },
    {
      label: 'Analytics',
      path: '/analytics-dashboard',
      icon: 'BarChart',
      tooltip: 'Accédez à votre tableau d\'analyse',
      roles: ['counselor', 'parent']
    }
  ];

  const navigationItems = allNavigationItems?.filter(item => 
    item?.roles?.includes(userRole)
  );

  const isActive = (path) => location?.pathname === path;

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const currentUser = {
    name: userProfile?.full_name || user?.email?.split('@')?.[0] || 'Utilisateur',
    level: `Niveau ${Math.min(Math.floor((userProfile?.achievements_count || 0) / 2) + 1, 5)} - ${(userProfile?.achievements_count || 0) > 10 ? 'Expert' : (userProfile?.achievements_count || 0) > 5 ? 'Explorateur' : 'Débutant'}`,
    initials: userProfile?.full_name ? (userProfile?.full_name?.trim()?.split(' ')?.length === 1 ? userProfile?.full_name?.charAt(0)?.toUpperCase() : (userProfile?.full_name?.trim()?.split(' ')?.[0]?.charAt(0) + userProfile?.full_name?.trim()?.split(' ')?.[userProfile?.full_name?.trim()?.split(' ')?.length - 1]?.charAt(0))?.toUpperCase()) : 'U',
    progress: userProfile?.progress_percentage || 0
  };

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/user-login';
  };

  return (
    <>
      <nav className="navigation-bar">
        <div className="navigation-bar-container">
          <Link to="/student-dashboard" className="navigation-bar-logo">
            <div className="navigation-bar-logo-icon">
              <Icon name="Compass" size={32} className="text-blue-600" />
            </div>
            <span className="navigation-bar-logo-text">CURSUS Orientation</span>
          </Link>

          <div className="navigation-bar-menu">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`navigation-bar-menu-item ${isActive(item?.path) ? 'active' : ''}`}
                title={item?.tooltip}
              >
                <Icon name={item?.icon} size={20} />
                <span>{item?.label}</span>
              </Link>
            ))}
            {!userRole && (
              <>
                <Link
                  to="/registration-role-selection"
                  className="text-gray-700 hover:text-blue-600 transition-colors duration-300 font-medium"
                >
                  S'inscrire
                </Link>
                <Link
                  to="/user-login"
                  className="text-gray-700 hover:text-blue-600 transition-colors duration-300 font-medium"
                >
                  Se Connecter
                </Link>
              </>
            )}
          </div>

          <div className="navigation-bar-right">
            <div className="relative">
              <div onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}>
                <UserProfileIndicator user={currentUser} />
              </div>
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    Profil
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                  >
                    Déconnexion
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
      <button
        className="mobile-menu-toggle"
        onClick={handleMobileMenuToggle}
        aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        aria-expanded={isMobileMenuOpen}
      >
        <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
      </button>
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={handleMobileMenuClose}
        navigationItems={navigationItems}
        currentPath={location?.pathname}
      />
    </>
  );
};

export default NavigationBar;